import type { SettingsPage } from "../types";

/**
 * Image insertion defaults. Read non-reactively at insert time via
 * getSettingValue("images", key) (see src/editor/imageInsert.ts and
 * src/editor/imageIngest.ts) so changing a value applies to the next insert
 * without an app restart.
 */
const imageSettings: SettingsPage = {
  id: "images",
  label: "Images",
  order: 2,
  items: [
    {
      key: "placementHeading",
      label: "Placement",
      type: "heading",
      default: "",
    },
    {
      key: "defaultPlacement",
      label: "Default image placement",
      type: "select",
      default: "block",
      options: [
        { value: "block", label: "Block (its own line)" },
        { value: "inline", label: "Inline (flows with text)" },
      ],
      description:
        "Where a newly inserted image sits. You can still switch an individual image later from Insert > Toggle image inline/block.",
    },
    {
      key: "sizeHeading",
      label: "Size",
      type: "heading",
      default: "",
    },
    {
      key: "downscale",
      label: "Downscale large images",
      type: "toggle",
      default: true,
      description:
        "Re-encode oversized images (e.g. phone photos) down to the max dimension below before storing, to keep documents and sync small.",
    },
    {
      key: "maxDimension",
      label: "Max image dimension (px)",
      type: "number",
      default: 1600,
      min: 256,
      max: 8000,
      step: 64,
      description: "Longest edge an image is scaled down to when downscaling is on.",
      visibleWhen: { key: "downscale", equals: true },
    },
    {
      key: "jpegQuality",
      label: "Image quality",
      type: "number",
      default: 0.85,
      min: 0.1,
      max: 1,
      step: 0.05,
      description: "JPEG re-encode quality when downscaling. Lower = smaller files, more compression artifacts.",
      visibleWhen: { key: "downscale", equals: true },
    },
    {
      key: "defaultInsertedWidth",
      label: "Default inserted image width (px)",
      type: "number",
      default: 640,
      min: 100,
      max: 2000,
      step: 20,
      description: "Initial on-screen width for a newly inserted image (never upscaled past its natural size).",
    },
  ],
};

export default imageSettings;
